import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { RevShareEntry } from '../../../models/RevShareData';
import {
  calculateRevShareAmount,
  getRevShareMethod,
} from '../../../data/rev-share/helpers/revShareSettingsUtils';
import {
  formatEffectiveRange,
  getEntryStatus,
} from '../../../data/rev-share/helpers/revShareEntryUtils';
import { formatTwoDecimalPlaces } from '../../../helpers/utilityFunctions';
import { IconButtonMenu } from '../../../components/IconButtonMenu';
import ExpandIcon from '../../../components/ExpandIcon';
import { Svgs } from '../../../assets/svg';

interface RevShareEntriesTableProps {
  entries: RevShareEntry[];
  isOrgPays?: boolean;
  isInherited?: boolean;
  onViewEdit: (entry: RevShareEntry) => void;
  onRemove: (entry: RevShareEntry) => void;
}

const STATUS_COLUMN_WIDTH = '120px';
const ACTIONS_COLUMN_WIDTH = '55px';

const columnGroup = (
  <colgroup>
    <col style={{ width: STATUS_COLUMN_WIDTH }} />
    <col />
    <col />
    <col style={{ width: ACTIONS_COLUMN_WIDTH }} />
  </colgroup>
);

const RevShareEntriesTable: React.FC<RevShareEntriesTableProps> = ({
  entries,
  isInherited,
  onViewEdit,
  onRemove,
}) => {
  const theme = useTheme();
  const today = useMemo(() => new Date(), []);
  const [showExpired, setShowExpired] = useState(false);

  const activeAndScheduled = useMemo(
    () => entries.filter(e => getEntryStatus(e, today) !== 'Expired'),
    [entries, today],
  );

  const expiredEntries = useMemo(
    () => entries.filter(e => getEntryStatus(e, today) === 'Expired'),
    [entries, today],
  );

  if (entries.length === 0) return null;

  const renderRow = (
    entry: RevShareEntry,
    index: number,
    list: RevShareEntry[],
    isLastSection: boolean,
  ) => {
    const status = getEntryStatus(entry, today);
    const revShareAmount = calculateRevShareAmount(
      getRevShareMethod(entry),
      entry.baseAmount,
      entry.revSharePct,
      entry.fixedAmount,
    );
    const isLastRow = index === list.length - 1;

    return (
      <TableRow
        key={entry.id}
        sx={{
          '& td': {
            borderBottom: isLastRow ? 'none' : `1px solid ${theme.palette.secondary.disabled}`,
            pt: 1.5,
            pb: isLastRow && isLastSection ? 0 : 1.5,
          },
        }}>
        <TableCell>
          <Link
            component='button'
            underline='none'
            variant='p16'
            onClick={() => onViewEdit(entry)}
            sx={{
              'color':
                status === 'Active' ? theme.palette.primary.main : theme.palette.secondary.main,
              'fontStyle': status === 'Scheduled' ? 'italic' : 'normal',
              '&:hover': { opacity: 0.7 },
              'cursor': 'pointer',
            }}>
            {status}
          </Link>
        </TableCell>
        <TableCell>
          <Typography variant='p16' color='secondary.main'>
            {formatEffectiveRange(entry)}
          </Typography>
        </TableCell>
        <TableCell align='right'>
          {entry.disabledRevShare ? (
            <Typography variant='p16' color='secondary.main'>
              none
            </Typography>
          ) : (
            <Box display='inline-flex' alignItems='baseline'>
              <Box mr='2px'>
                <Typography component='span' variant='p20SemiBold' color='info.main'>
                  $
                </Typography>
                <Typography component='span' variant='p20SemiBold' color='secondary.dark'>
                  {formatTwoDecimalPlaces(revShareAmount)}
                </Typography>
              </Box>
              <Typography component='span' variant='p16' color='secondary.main' lineHeight={1}>
                /sale
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell align='right'>
          <IconButtonMenu
            icon={
              <Svgs.ActionSmallEllipsisDefault
                style={{
                  color: theme.palette.primary.main,
                }}
              />
            }
            disableRipple
            menuProps={{ PaperProps: { sx: { minWidth: 0 } } }}
            sx={{ 'p': 0, '&:hover': { backgroundColor: 'transparent' } }}
            items={
              isInherited
                ? [
                    {
                      label: 'View',
                      onClick: () => onViewEdit(entry),
                    },
                  ]
                : [
                    {
                      label: 'View/edit',
                      onClick: () => onViewEdit(entry),
                    },
                    {
                      label: 'Remove',
                      onClick: () => onRemove(entry),
                      sx: { color: 'error.dark' },
                    },
                  ]
            }
          />
        </TableCell>
      </TableRow>
    );
  };

  const hasExpired = expiredEntries.length > 0;
  const hasActiveOrScheduled = activeAndScheduled.length > 0;

  const tableHead = (
    <TableHead>
      <TableRow sx={{ '& th': { borderBottom: 'none', pt: 0, pb: 0 } }}>
        <TableCell>
          <Typography variant='p12Light' color='secondary.main'>
            Status
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='p12Light' color='secondary.main'>
            Effective
          </Typography>
        </TableCell>
        <TableCell align='right'>
          <Typography variant='p12Light' color='secondary.main'>
            Fee Share
          </Typography>
        </TableCell>
        <TableCell align='right'>
          <Typography variant='p12Light' color='secondary.main'>
            Actions
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <Box>
      {hasActiveOrScheduled && (
        <TableContainer>
          <Table size='small'>
            {columnGroup}
            {tableHead}
            <TableBody>
              {activeAndScheduled.map((entry, index) =>
                renderRow(entry, index, activeAndScheduled, !hasExpired),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasExpired && (
        <Box mt={2} mb={showExpired ? 2 : 0} display='flex' alignItems='center'>
          <ExpandIcon
            isExpanded={showExpired}
            onToggle={() => setShowExpired(prev => !prev)}
            sx={{ p: 0, mr: 1 }}
          />
          <Link
            component='button'
            underline='none'
            onClick={() => setShowExpired(prev => !prev)}
            sx={{ cursor: 'pointer' }}>
            <Typography variant='p16Light' color='secondary.main'>
              {showExpired ? 'Hide expired' : 'Show expired'}
            </Typography>
          </Link>
        </Box>
      )}

      {hasExpired && (
        <Collapse in={showExpired} timeout={200} unmountOnExit>
          <TableContainer>
            <Table size='small'>
              {columnGroup}
              {tableHead}
              <TableBody>
                {expiredEntries.map((entry, index) =>
                  renderRow(entry, index, expiredEntries, true),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      )}
    </Box>
  );
};

export default RevShareEntriesTable;
